import React, { FC, useContext, useEffect } from 'react';
import sanitizeHtml from 'sanitize-html';

import UserDetailsContext from 'Context/userDetailsContext';
import NewList from 'Components/SmartList/NewList';
import { areEqual } from 'Utils/areEqual';
import { useSelector, useDispatch } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useMutation } from '@apollo/react-hooks';
import { toastMessage } from 'Containers/Toast/actions';
// @ts-ignore
import commaNumber from 'comma-number';
import { numberWithCommas } from 'Utils/numberWithCommas';
import { undefinedReplacer } from 'Containers/Filter/utilities/filter.utilities';
import { setNewListValue } from 'Containers/Filter/ListMembership/actions/listMembership.actions';
import withErrorBoundary from 'HOC/ErrorBoundary';
import { useFocusManager } from '@react-aria/focus';
import {
  createListGql,
  appendUsersToListGql,
  duplicateListGql,
} from './gqlQuery';

interface Props {
  closeModal?: () => void;
  checkedItems?: any;
  userIds: any[];
  fromListId?: string;
  filters?: any;
  searchText?: string;
}

const NewListContainer: FC<Props> = (props: Props) => {
  // Logged in user details //
  const userDetails = useContext(UserDetailsContext);
  const { filter } = useSelector((selectors: any) => selectors, areEqual);
  // GraphQL Queries / responses //
  const [
    createNewList,
    {
      loading: createListLoading,
      error: createListError,
      data: createListData,
    },
  ] = useMutation(createListGql);

  const { closeModal, checkedItems, fromListId, filters, searchText, userIds } =
    props;

  const profiles = [];

  checkedItems.forEach((item: string, idx: number) =>
    profiles.push({ userkey: item, id: userIds[idx] })
  );

  const [duplicateList, { loading: dupLoading, data: dupData }] =
    useMutation(duplicateListGql);

  const [appendUsers, { data: appendUsersData }] =
    useMutation(appendUsersToListGql);
  const response = !!createListData;

  const Dispatch = useDispatch();
  const onCreateList = (name: string) => {
    if (!createListLoading && createListData === undefined)
      createNewList({
        variables: {
          inst_id: userDetails.instId,
          account_id: userDetails.accountId,
          name,
          listType: checkedItems.length <= 0 ? 'filtered' : 'custom',
          filters: filters.filtersv2,
          attributes:
            checkedItems.length <= 0
              ? JSON.stringify(
                  {
                    // We don't need the query property currently in the filters
                    demographics: filter.demographics,
                    prospectProfile: filter.prospectProfile,
                    engagement: filter.engagement,
                    listMembership: filter.listMembership,
                    locations: filter.locations,
                  },
                  undefinedReplacer
                )
              : '',
          createUser: userDetails.accountId,
          loadAttributes: JSON.stringify(
            {
              // We don't need the query property currently in the filters
              demographics: filter.demographics,
              prospectProfile: filter.prospectProfile,
              engagement: filter.engagement,
              listMembership: filter.listMembership,
              locations: filter.locations,
            },
            undefinedReplacer
          ),
          listMembership: filters.listMembership,
          audiencesSearch: searchText,
        },
      });

    // Alert the Filter that a new list was made so that it can refetch
    setNewListValue(Dispatch, true);
  };

  const onduplicateList = (name: string) => {
    if (!dupLoading && dupData === undefined) {
      duplicateList({
        variables: {
          inst_id: userDetails.instId,
          account_id: userDetails.accountId,
          name: `${name}`,
          id: fromListId,
          audiencesSearch: searchText,
        },
      });
    }
    setNewListValue(Dispatch, true);
  };

  useEffect(() => {
    if (
      response &&
      createListData.listCreate.code >= 200 &&
      createListData.listCreate.code <= 299
    ) {
      // Currently there is no pagination on list, use console to verify save //
      if (checkedItems.length <= 0) {
        const count = commaNumber(createListData.listCreate.data.count);
        Dispatch(
          toastMessage({
            message: sanitizeHtml(
              `Added ${numberWithCommas(count)} Inquiries to <b>${
                createListData.listCreate.data.name
              }</b>`
            ),
          })
        );
        closeModal();
      } else {
        // If listTyle is custom, a second call to add userKeys must be passed //
        appendUsers({
          variables: {
            inst_id: userDetails.instId,
            account_id: userDetails.accountId,
            id: createListData.listCreate.data.id,
            profiles,
          },
        });
      }
    }
  }, [createListData]);

  useEffect(() => {
    if (dupData) {
      if (
        dupData.listDuplicate.code >= 200 &&
        dupData.listDuplicate.code <= 299
      ) {
        const count = commaNumber(dupData.listDuplicate.data.count);
        Dispatch(
          toastMessage({
            message: sanitizeHtml(
              `Added ${numberWithCommas(count)} Inquiries to <b>${
                dupData.listDuplicate.data.name
              }</b>`
            ),
          })
        );
        closeModal();
      }
    }
  }, [dupData]);

  useEffect(() => {
    if (appendUsersData) {
      Dispatch(
        toastMessage({
          message: `Added ${numberWithCommas(
            appendUsersData.listAudienceAppendIds.data.count
          )} Inquiries to <b>${createListData.listCreate.data.name}</b>`,
          position: 'bottom',
        })
      );
      closeModal();
    }
  }, [appendUsersData]);

  // This fixes focus getting out of Modal Dialog when user switches between "Existing List" and "Create New",
  // while using screen reader (ChromeVox). It doesn't happen if we don't use screen reader.
  const focusManager = useFocusManager();
  useEffect(() => {
    if (focusManager) {
      focusManager.focusFirst();
    }
  }, [focusManager]);

  const onCreate = (name: string) => {
    if (fromListId === '-1' || checkedItems.length > 0) onCreateList(name);
    else onduplicateList(name);
  };
  return (
    <NewList
      disabled={response || createListLoading}
      errorMessage={
        createListError
          ? JSON.parse(createListError.networkError.result.errors[0].message)
              .serverResponse.message
          : null
      }
      onCreate={onCreate}
      onCancel={closeModal}
      checkedItems={checkedItems.length}
    />
  );
};

NewListContainer.defaultProps = {
  closeModal: () => {},
  checkedItems: [],
  fromListId: '',
  filters: '',
  searchText: '',
};
export default withErrorBoundary(NewListContainer);
